import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { StoryCard, NewStoryCard } from '../../components/StoryCard';
import { LoadingIndicator } from '../../components/ui';
import './StoryCardList.css';

/**
 * StoryCardList container component
 * Displays a list of stories with pagination
 * 
 * @param {Object} props - Component props
 * @param {Array} props.stories - Array of story objects to display
 * @param {boolean} props.isLoading - Whether stories are currently loading
 * @param {string} props.error - Error message if stories failed to load
 * @param {function} props.onStorySelect - Called when a story is selected
 * @param {function} props.onNewStory - Called when new story button is clicked
 */
const StoryCardList = ({ 
  stories: storyList = [], 
  isLoading = false, 
  error = null, 
  onStorySelect, 
  onNewStory 
}) => {
  const [currentPage, setCurrentPage] = useState(0);
  const ITEMS_PER_PAGE = 9; // 9 story cards + 1 new story card = 10 total
  
  // Sort stories by when they were last updated (newest first)
  const sortedStories = useMemo(() => {
    if (!storyList || storyList.length === 0) {
      return [];
    }
    
    return [...storyList].sort((a, b) => {
      // Get the pages of each story
      const aPages = a.pages || [];
      const bPages = b.pages || [];
      
      // If one has pages and the other doesn't, the one with pages is newer
      if (aPages.length > 0 && bPages.length === 0) return -1;
      if (aPages.length === 0 && bPages.length > 0) return 1;
      
      // If both have pages, compare by the highest page ID (which correlates to creation time)
      // Higher IDs are more recent
      const aLatestPageId = aPages.length > 0 ? Math.max(...aPages.map(p => p.id)) : 0;
      const bLatestPageId = bPages.length > 0 ? Math.max(...bPages.map(p => p.id)) : 0;
      
      if (aLatestPageId !== bLatestPageId) {
        return bLatestPageId - aLatestPageId; // Higher ID = more recent
      }
      
      // If they have the same latest page ID (unlikely), use story ID as a tiebreaker
      return b.id - a.id;
    });
  }, [storyList]);

  // Handle story selection
  const handleStorySelect = (storyId) => {
    if (onStorySelect) {
      onStorySelect(storyId);
    }
  };

  // Handle new story creation
  const handleNewStory = () => {
    if (onNewStory) {
      onNewStory();
    }
  };

  if (isLoading) {
    return (
      <div className="story-card-list story-card-list--loading" data-testid="story-list-loading">
        <LoadingIndicator size="large" label="Loading stories..." />
      </div>
    );
  }

  if (error && (!sortedStories || sortedStories.length === 0)) {
    return (
      <div className="story-card-list story-card-list--error" data-testid="story-list-error">
        <p className="story-card-list__error">{error}</p>
        <NewStoryCard onClick={handleNewStory} />
      </div>
    );
  }

  // Calculate pagination
  const totalPages = Math.ceil(sortedStories.length / ITEMS_PER_PAGE);
  const startIndex = currentPage * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentStories = sortedStories.slice(startIndex, endIndex);
  
  // Navigation handlers
  const goToNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };
  
  const goToPreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };
  
  return (
    <div className="story-card-list-container" data-testid="story-card-list">
      <div className="story-card-list">
        {/* Always show the new story card on all pages */}
        <NewStoryCard onClick={handleNewStory} data-testid="new-story-card" />
        
        {/* Only show stories for the current page */}
        {currentStories.map(story => (
          <StoryCard 
            key={story.id} 
            story={story} 
            onClick={handleStorySelect} 
            data-testid={`story-card-${story.id}`}
          />
        ))}
      </div>
      
      {/* Show pagination navigation if we have more than one page */}
      {totalPages > 1 && (
        <div className="story-card-list__pagination">
          <button 
            className="story-card-list__nav-arrow story-card-list__nav-arrow--prev"
            onClick={goToPreviousPage}
            disabled={currentPage === 0}
            aria-label="Previous page"
            data-testid="prev-page-button"
          >
            ←
          </button>
          
          <span className="story-card-list__page-indicator" data-testid="page-indicator">
            {currentPage + 1} / {totalPages}
          </span>
          
          <button 
            className="story-card-list__nav-arrow story-card-list__nav-arrow--next"
            onClick={goToNextPage}
            disabled={currentPage === totalPages - 1}
            aria-label="Next page"
            data-testid="next-page-button"
          >
            →
          </button>
        </div>
      )}
    </div>
  );
};

StoryCardList.propTypes = {
  stories: PropTypes.array,
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  onStorySelect: PropTypes.func.isRequired,
  onNewStory: PropTypes.func.isRequired
};

export default StoryCardList;